import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { CaseManagementService } from "src/app/services/case-management.service";
import { UtilsService } from "src/app/edge/utils.service";
import { TranslateService } from "@ngx-translate/core";
import { CaseCategoriesActions } from '@states/case-category/case-category.actions';
import { switchMap, share, map, catchError, mergeMap } from "rxjs";
import { SharedActions } from "@states/shared/shared.action-types";
import { LoggerService } from "src/app/services/logger.service";
import { ContextualLoggerService } from "src/app/services/contextual-logger.service";

@Injectable()
export class CaseCategoryEffect {
    private readonly actions$ = inject(Actions);
    private readonly caseManagementService = inject(CaseManagementService);
    private readonly utilService = inject(UtilsService);
    private readonly translateService = inject(TranslateService);
    private baseLogger = inject(LoggerService);
    private context = 'CaseCategoriesEffect';
    private logger = new ContextualLoggerService(this.baseLogger, this.context);

    public startLoadingCategoriesList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CaseCategoriesActions.startLoadingCategoriesList),
            map(() => CaseCategoriesActions.getCategoriesList())
        )
    )

    public getCases$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CaseCategoriesActions.getCategoriesList),
            switchMap(() => {
                return this.caseManagementService.getAllCategories()
                    .pipe(
                        map((categories) => CaseCategoriesActions.getCategoriesListSuccess({ categories })),
                        catchError((error) => {
                            const errorMessage = this.utilService.errMessage(error);
                            this.logger.error(errorMessage);

                            return [
                                CaseCategoriesActions.getCategoriesListFail(),
                            ]
                        })
                    )
            }),
            share(),
        ),
    );

    public createCase$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CaseCategoriesActions.createCategory),
            switchMap(({ form }) => this.caseManagementService.createCategory(form)
                .pipe(
                    mergeMap((category) => [
                        CaseCategoriesActions.createCategorySuccess({ category: category }),
                        SharedActions.showMessage({ success: this.translateService.instant('CASE_CATEGORIES.CREATE_SUCCESS') })
                    ]),
                    catchError((error) => [
                        CaseCategoriesActions.createCategoryFail(),
                        SharedActions.showMessage({ error: this.utilService.errMessage(error) })
                    ])
                )
            ),
            share(),
        )
    );

    public updateCase$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CaseCategoriesActions.updateCategory),
            switchMap(({ id, form }) => this.caseManagementService.updateCategory(id, form)
                .pipe(
                    mergeMap(() => [
                        CaseCategoriesActions.updateCategorySuccess({ id, form }),
                        SharedActions.showMessage({ success: this.translateService.instant('CASE_CATEGORIES.EDIT_SUCCESS') })
                    ]),
                    catchError((error) => [
                        CaseCategoriesActions.updateCategoryFail(),
                        SharedActions.showMessage({ error: this.utilService.errMessage(error) })
                    ])
                )
            ),
            share(),
        )
    );

    public deleteCase$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CaseCategoriesActions.deleteCategory),
            switchMap(({ id }) =>
                this.caseManagementService.deleteCategory(id)
                    .pipe(
                        map(() => CaseCategoriesActions.deleteCategorySuccess({ id })),
                        catchError((error) => [
                            CaseCategoriesActions.deleteCategoryFail(),
                            SharedActions.showMessage({ error: this.utilService.errMessage(error) })
                        ])
                    )
            ),
            share(),
        )
    );
}
